import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { useAppSelector } from '@/store/store-hooks'
import { selectUploadDrawerFiles } from '../store/selectors'

export type ProcessingSummary = {
  /**
   * Total number of files visible in the upload drawer.
   */
  totalCount: number
  /**
   * Number of files that have finished processing successfully (`processing_status` === `COMPLETED`).
   */
  readyCount: number
  /**
   * Number of files that have failed (either upload failed or processing ended in an error state).
   */
  failedCount: number
  /**
   * Aggregate status for upload/processing outcomes.
   */
  status: 'empty' | 'processing' | 'ready' | 'partial_failure' | 'failed'
}

export function useProcessingSummary(): ProcessingSummary {
  const processingFiles = useAppSelector(selectUploadDrawerFiles)

  const totalCount = processingFiles.length

  const readyCount = processingFiles.filter((f) => f.backend?.record.processing_status === UserFileProcessingStatus.Completed).length

  const failedCount = processingFiles.filter((f) => {
    if (f.client.uploadStatus === 'unsupported_type') return true
    if (f.client.uploadStatus === 'error') return true
    const processingStatus = f.backend?.record.processing_status
    return processingStatus === UserFileProcessingStatus.Failed
  }).length

  const allReady = totalCount > 0 && readyCount === totalCount
  const allComplete = totalCount > 0 && readyCount + failedCount === totalCount
  const allFailed = allComplete && failedCount === totalCount
  const hasSomeIssues = allComplete && failedCount > 0 && failedCount !== totalCount

  const status: ProcessingSummary['status'] =
    totalCount === 0 ? 'empty' : allReady ? 'ready' : allFailed ? 'failed' : hasSomeIssues ? 'partial_failure' : 'processing'

  return {
    totalCount,
    readyCount,
    failedCount,
    status,
  }
}
